/**
 * @module services/aiRouting
 * @description AI mode routing service for EmberPOS.
 * Manages which AI backend is active (RAG, Swarm, or built-in local answers) with
 * localStorage persistence and server-side sync via the settings API.
 * Validates modes and falls back to 'rag' when an invalid or unavailable
 * mode is requested.
 */

import { apiFetch } from '../lib/api';

/** @type {Set<string>} Valid AI mode identifiers */
const VALID_AI_MODES = new Set(['rag', 'swarm', 'local']);

/** @type {string} localStorage key for persisting the AI mode */
const AI_MODE_STORAGE_KEY = 'firepos_ai_mode';

/**
 * Check whether Swarm AI mode is enabled via environment variable.
 * @returns {boolean} True if VITE_SWARM_ENABLED is 'true'
 */
export const getSwarmEnabled = () => import.meta.env.VITE_SWARM_ENABLED === 'true';

/**
 * Get the Swarm API endpoint URL from environment or default.
 * @returns {string} Swarm endpoint URL
 */
export const getSwarmEndpoint = () =>
  import.meta.env.VITE_SWARM_ENDPOINT || 'https://apps.quantrolix.ai/api/menu-flashcard';

/**
 * Normalize and validate an AI mode string.
 * @param {*} mode - Raw mode value to normalize
 * @returns {string|null} Normalized mode string, or null if invalid.
 *   Returns 'rag' instead of 'swarm' when Swarm is disabled.
 */
const normalizeAiMode = (mode) => {
  const normalized = typeof mode === 'string' ? mode.trim().toLowerCase() : '';
  if (!VALID_AI_MODES.has(normalized)) return null;
  if (normalized === 'swarm' && !getSwarmEnabled()) return 'rag';
  return normalized;
};

/**
 * Get the current AI mode from localStorage.
 * @returns {string} Current AI mode ('rag', 'swarm', or 'local'). Defaults to 'rag'.
 */
export const getAiMode = () => {
  if (typeof window === 'undefined') return 'rag';
  const stored = window.localStorage.getItem(AI_MODE_STORAGE_KEY);
  return normalizeAiMode(stored) || 'rag';
};

/**
 * Set the AI mode in localStorage (client-side only, no server sync).
 * @param {string} mode - AI mode to set
 * @returns {string} The normalized mode that was actually stored
 */
export const setAiMode = (mode) => {
  if (typeof window === 'undefined') return 'rag';
  const normalized = normalizeAiMode(mode);
  if (!normalized) return getAiMode();
  window.localStorage.setItem(AI_MODE_STORAGE_KEY, normalized);
  return normalized;
};

/**
 * Fetch the AI mode from the server settings API and sync to localStorage.
 * Falls back to the current localStorage value on network errors.
 * @returns {Promise<string>} The resolved AI mode
 */
export const fetchAiMode = async () => {
  if (typeof window === 'undefined') return 'rag';
  try {
    const response = await apiFetch(`/api/settings/ai-mode`);
    if (!response.ok) return getAiMode();
    const data = await response.json();
    const normalized = normalizeAiMode(data?.aiMode);
    if (!normalized) return getAiMode();
    window.localStorage.setItem(AI_MODE_STORAGE_KEY, normalized);
    return normalized;
  } catch {
    return getAiMode();
  }
};

/**
 * Persist the AI mode to both localStorage and the server settings API.
 * Updates localStorage immediately and then syncs with the server.
 * Falls back gracefully if the server request fails.
 * @param {string} mode - AI mode to persist
 * @returns {Promise<string>} The confirmed AI mode after server sync
 */
export const persistAiMode = async (mode) => {
  const normalized = setAiMode(mode);
  if (typeof window === 'undefined') return normalized;

  try {
    const response = await apiFetch(`/api/settings/ai-mode`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ aiMode: normalized }),
    });
    if (!response.ok) return normalized;
    const data = await response.json();
    const serverMode = normalizeAiMode(data?.aiMode);
    if (!serverMode) return normalized;
    window.localStorage.setItem(AI_MODE_STORAGE_KEY, serverMode);
    return serverMode;
  } catch {
    return normalized;
  }
};

export default {
  getAiMode,
  setAiMode,
  fetchAiMode,
  persistAiMode,
  getSwarmEnabled,
  getSwarmEndpoint,
};
